import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Chip,
} from "@mui/material";
import apiService from "../../../services/api";
import ImagePreviewCard from "../../../components/common/ImagePreviewCard";
import DefaultImage from "../../../assets/images/DefaultImage.png";
import { DATE_FORMATS } from "../../../constants";
import CustomLoading from "../../../components/common/CustomLoading";
import useCodeCommonByCategory from "../../../hooks/useCodeCommonByCategory";

const BASE_IMAGE_URL = `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(/\/?$/, '/')}`;
const DEFAULT_IMAGE = "default.png";

const StaffDetails: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [staffData, setStaffData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch staff status codes
  const { getDisplayNameByCodeValue } = useCodeCommonByCategory('StaffStatus');

  // Build preview src from value that can be a URL, path, or JSON array string
  const resolvePreviewSrc = (value: string): string => {
    try {
      const v = String(value || "").trim();
      if (!v) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
      if (v.startsWith("[")) {
        const arr = JSON.parse(v);
        if (Array.isArray(arr) && arr.length > 0) {
          const first = String(arr[0] || "");
          if (!first) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
          if (first.startsWith('http')) return first;
          const cleaned = first.replace(/^\/+/, '');
          return `${BASE_IMAGE_URL}${cleaned}`;
        }
      }
      if (v.startsWith('http')) return v;
      const cleaned = v.replace(/^\/+/, '');
      return `${BASE_IMAGE_URL}${cleaned || DEFAULT_IMAGE}`;
    } catch {
      return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
    }
  };

  useEffect(() => {
    if (id) {
      fetchStaffDetails(id);
    }
  }, [id]);

  const fetchStaffDetails = async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getUserById(userId);
      const userData = (response as any)?.data || response;
      setStaffData(userData);
    } catch (error: any) {
      setError("Failed to load staff details. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const getStatusStyle = (statusDisplayName: string) => {
    // Align with StaffManagementPage chip colors
    const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
      Active: { bg: "rgba(212, 255, 199, 1)", color: "rgba(31, 113, 13, 1)" },
      InActive: { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
      Pending: { bg: "rgba(255, 245, 157, 1)", color: "rgba(107, 105, 14, 1)" },
      Suspended: { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
      Banned: { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
    };
    return STATUS_STYLES[statusDisplayName] || { bg: "#e9ecef", color: "#495057" };
  };

  if (loading) {
    return <CustomLoading message="Fetching Staff Details..." />;
  }

  if (error) {
    return (
      <Container fluid>
        <Alert severity="error" className="mb-3">
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate("/staff-management", { state: location.state })}>
          Go Back
        </Button>
      </Container>
    );
  }

  if (!staffData) {
    return (
      <Container fluid>
        <Alert severity="warning" className="mb-3">
          No staff data found.
        </Alert>
        <Button variant="outlined" onClick={() => navigate("/staff-management", { state: location.state })}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="pb-5">
      {/* Basic Information Section */}
      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          {/* Header with Status */}
          <Box className="d-flex justify-content-between align-items-center mb-3">
            <Typography
              variant="h6"
              className="fw-bold mb-0"
              sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
            >
              Basic Information
            </Typography>
              {(() => {
                const statusDisplayName = getDisplayNameByCodeValue(staffData.staffstatus) || "Unknown";
                const style = getStatusStyle(statusDisplayName);
                return (
                  <Chip
                    label={statusDisplayName}
                    size="medium"
                    sx={{
                      backgroundColor: style.bg,
                      color: style.color,
                      fontWeight: 600,
                    }}
                  />
                );
              })()}
          </Box>

          {/* Divider Line */}
          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          {/* Basic Information Grid */}
          <Row className="g-4">
            {[
              { label: "Name", value: staffData.name || "N/A" },
              { label: "Username", value: staffData.username || "N/A" },
              { label: "Emp Code", value: staffData.empcode || "N/A" },
              { label: "Role", value: staffData.rolename || "N/A" },
              { label: "Hub Name", value: staffData.hubname || "N/A" },
              { label: "Phone Number", value: staffData.phonenumber || "N/A" },
              { label: "Email", value: staffData.emailid || staffData.email || "N/A" },
              { label: "Date of Birth", value: DATE_FORMATS.formatDate(staffData.dob) || "N/A" },
              { label: "Joining Date", value: DATE_FORMATS.formatDate(staffData.employmentdetail?.joiningdate) || "N/A" },
            ].map((field, idx) => (
              <Col xs={12} sm={6} md={4} key={idx}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: "#6c757d", 
                      mb: 0.5,
                      fontSize: "0.85rem",
                      fontWeight: 500
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="fw-bold"
                    sx={{ 
                      color: "#495057",
                      fontSize: "0.95rem",
                      wordBreak: "break-word"
                    }}
                  >
                    {field.value}
                  </Typography>
                </Box>
              </Col>
            ))}
          </Row>
        </Box>
      </Paper>

      {/* Employment Details */}
      {staffData.employmentdetail && (
        <Paper className="mb-4 shadow-sm border-0 rounded-3">
          <Box className="px-4 pt-4 pb-4">
            {/* Header */}
            <Typography
              variant="h6"
              className="fw-bold mb-3"
              sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
            >
              Employment Details
            </Typography>

            {/* Divider Line */}
            <Box
              sx={{
                height: "1px",
                backgroundColor: "#e9ecef",
                mb: 3,
              }}
            />

            {/* Employment Information Grid */}
            <Row className="g-4">
              {[
                { label: "Designation", value: staffData.employmentdetail.empdesignation || "N/A" },
                { label: "Joining Date", value: DATE_FORMATS.formatDate(staffData.employmentdetail.joiningdate) || "N/A" },
                { label: "Emergency Contact", value: staffData.employmentdetail.emergencycontact || "N/A" },
                { label: "E-Signature", value: staffData.employmentdetail.specimen_e_signature || "N/A" },
              ].map((field, idx) => (
                <Col xs={12} sm={6} md={3} key={idx}>
                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      variant="body2"
                      sx={{ 
                        color: "#6c757d", 
                        mb: 0.5,
                        fontSize: "0.85rem",
                        fontWeight: 500
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      className="fw-bold"
                      sx={{ 
                        color: "#495057",
                        fontSize: "0.95rem",
                        wordBreak: "break-word"
                      }}
                    >
                      {field.value}
                    </Typography>
                  </Box>
                </Col>
              ))}
              
            </Row>
          </Box>
        </Paper>
      )}

      {/* Attachments */}
      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          {/* Header */}
          <Typography
            variant="h6"
            className="fw-bold mb-3"
            sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
          >
            Attachments
          </Typography>

          {/* Divider Line */}
          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          {/* Attachments Grid */}
          <Row className="g-3">
            {(staffData.profilephoto || staffData.profileImage) && (
              <Col xs={12} sm={6} md={4}>
                <ImagePreviewCard
                  title="Profile Image"
                  value={staffData.profilephoto || staffData.profileImage}
                  resolveSrc={resolvePreviewSrc}
                  size={200}
                  defaultImage={DefaultImage}
                />
              </Col>
            )}

            {!staffData.profilephoto &&
              !staffData.profileImage && (
                <Col xs={12}>
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      backgroundColor: "#f8f9fa",
                      borderRadius: 2,
                      border: "2px dashed #dee2e6",
                    }}
                  >
                    <Typography variant="body1" sx={{ color: "#6c757d" }}>
                      No attachments available
                    </Typography>
                  </Box>
                </Col>
              )}
          </Row>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Paper
        className="mt-4 shadow-sm border-0 rounded-3"
        sx={{
          position: { xs: "fixed", md: "static" },
          bottom: { xs: 0, md: "auto" },
          left: 0,
          right: 0,
          zIndex: 1000,
        }}
      >
      </Paper>
    </Container>
  );
};


export default StaffDetails;
